import { useEffect } from "react";
import { FaBolt, FaChartBar, FaBrain, FaShieldAlt } from "react-icons/fa";
import "../assets/styles/AthenIAPage.css";
import BotaoContato from "../components/common/BotaoContato";
import Footer20 from "../components/common/Footer20";

/* ─────────────────────────────────────────
   DATA
───────────────────────────────────────── */
const plans = [
  {
    name: "Piloto",
    price: "R$ 0,00",
    note: "2 semanas para explorar o dashboard.",
    features: [
      "Dashboard básico de métricas",
      "Integração com 1 produto SummerTech",
      "1 relatório de desempenho",
      "Acesso por 2 semanas",
    ],
    cta: "Iniciar Piloto",
    popular: false,
    included: false,
    badge: null,
    color: "#16a34a",
    textColor: "#fff",
    waMessage: "Gostaria de ativar o produto AthenIA no plano Piloto",
  },
  {
    name: "Essencial",
    price: "R$ 119,90/mês",
    note: "Ou incluso gratuitamente no plano Pro de qualquer produto SummerTech.",
    features: [
      "Dashboard completo em tempo real",
      "Integração com até 2 produtos SummerTech",
      "Relatórios semanais automáticos",
      "Alertas de variação de desempenho",
      "Suporte básico",
    ],
    cta: "Assinar Essencial",
    popular: true,
    included: false,
    badge: { text: "Mais popular", style: "yellow" },
    color: "#FFC300",
    textColor: "#0d1b2a",
    waMessage: "Gostaria de ativar o produto AthenIA no plano Essencial",
  },
  {
    name: "Pro",
    price: "R$ 499,90/mês",
    note: "Ecossistema completo com todos os produtos e fontes externas integradas.",
    features: [
      "Todos os produtos SummerTech integrados",
      "Integração com CRM externo",
      "Relatórios mensais automáticos em PDF",
      "Alertas inteligentes por IA",
      "Funil completo — do lead à conversão",
      "Suporte prioritário",
    ],
    cta: "Assinar Pro",
    popular: false,
    included: true,
    badge: { text: "Ecossistema completo", style: "purple" },
    color: "#7c3aed",
    textColor: "#fff",
    waMessage: "Gostaria de ativar o produto AthenIA no plano Pro",
  },
];

const faqs = [
  {
    q: "Preciso ter outros produtos SummerTech para usar AthenIA?",
    a: "Não — AthenIA pode ser contratado de forma independente e também aceita dados de CRMs externos. Mas funciona melhor integrado ao ecossistema SummerTech.",
  },
  {
    q: "Se eu já assino o plano Pro de outro produto, ganho AthenIA?",
    a: "Sim! Assinantes do plano Pro de qualquer produto SummerTech recebem o AthenIA Essencial incluso, sem custo adicional.",
  },
  {
    q: "Os relatórios são gerados automaticamente?",
    a: "Sim — relatórios semanais (Essencial) e mensais em PDF (Pro) são enviados automaticamente para o seu e-mail ou WhatsApp.",
  },
  {
    q: "Consigo ver o funil completo do lead até a venda?",
    a: "No plano Pro sim — você visualiza toda a jornada: desde a captação pelo LeadIA, passando pela nutrição da NutrIA e atendimento da JulIA, até a conversão final.",
  },
];

/* ─────────────────────────────────────────
   HOOK
───────────────────────────────────────── */
function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll(".ah-reveal");
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("visible"); }),
      { threshold: 0.1 }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);
}

/* ─────────────────────────────────────────
   COMPONENTS
───────────────────────────────────────── */
function Navbar() {
  return (
    <nav className="ah-nav">
      <a href="/" className="ah-nav-logo">Summer<span>Tech</span></a>
      <a href="/" className="ah-nav-back">← Voltar para início</a>
      <a href="#cta" className="ah-nav-cta">Ativar AthenIA →</a>
    </nav>
  );
}

function MockupAthenia() {
  return (
    <div className="ah-hero-visual">
      <div className="ah-mockup">
        <div className="ah-mock-bar">
          <div className="ah-mock-dot" style={{ background: "#ff5f57" }} />
          <div className="ah-mock-dot" style={{ background: "#febc2e" }} />
          <div className="ah-mock-dot" style={{ background: "#28c840" }} />
          <span className="ah-mock-title">AthenIA · Painel de Inteligência</span>
        </div>
        <div className="ah-mock-body">
          <div className="ah-mock-sources">
            {[
              { label: "LeadIA", color: "#FFC300" },
              { label: "NutrIA", color: "#22c55e" },
              { label: "JulIA", color: "#a78bfa" },
              { label: "CRM Externo", color: "#93c5fd" },
            ].map((s) => (
              <div className="ah-mock-source" key={s.label}>
                <div className="ah-mock-source-dot" style={{ background: s.color }} />
                {s.label}
              </div>
            ))}
          </div>
          <div className="ah-mock-row">
            <span className="ah-mock-icon">📊</span>
            <div className="ah-mock-info">
              <span className="ah-mock-label">Taxa de conversão — esta semana</span>
              <span className="ah-mock-val">38% dos leads nutridos converteram</span>
            </div>
            <span className="ah-mock-tag ah-tag-green">↑ 12%</span>
          </div>
          <div className="ah-mock-row">
            <span className="ah-mock-icon">⚡</span>
            <div className="ah-mock-info">
              <span className="ah-mock-label">Alerta de oportunidade</span>
              <span className="ah-mock-val">Leads do Instagram convertem 2x mais</span>
            </div>
            <span className="ah-mock-tag ah-tag-yellow">Insight</span>
          </div>
          <div className="ah-mock-row">
            <span className="ah-mock-icon">📄</span>
            <div className="ah-mock-info">
              <span className="ah-mock-label">Relatório mensal gerado</span>
              <span className="ah-mock-val">PDF enviado para seu e-mail</span>
            </div>
            <span className="ah-mock-tag ah-tag-purple">Enviado</span>
          </div>
          <div className="ah-mock-stats">
            <div className="ah-mock-stat">
              <span className="ah-mock-stat-num">214</span>
              <span className="ah-mock-stat-lbl">Leads total</span>
            </div>
            <div className="ah-mock-stat">
              <span className="ah-mock-stat-num">38%</span>
              <span className="ah-mock-stat-lbl">Conversão</span>
            </div>
            <div className="ah-mock-stat">
              <span className="ah-mock-stat-num">R$4,2k</span>
              <span className="ah-mock-stat-lbl">ROI estimado</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function HeroSection() {
  return (
    <section className="ah-hero">
      <div className="ah-hero-glow" />
      <div className="ah-hero-glow2" />
      <div className="ah-hero-inner">
        <div className="ah-hero-text">
          <span className="ah-hero-badge">
            <span className="ah-badge-dot" />
            Novo produto · SummerTech
          </span>
          <h1><span className="ah-accent">Athen</span>IA</h1>
          <p className="ah-hero-tagline">A inteligência por trás de todo o seu ecossistema</p>
          <p>
            AthenIA consolida dados de todos os seus produtos SummerTech — LeadIA, NutrIA e JulIA —
            em um único painel de inteligência, entregando insights em tempo real e relatórios
            automáticos para que você tome decisões com clareza.
          </p>
          <div className="ah-hero-actions">
            <a href="#cta" className="ah-btn-primary">Ativar AthenIA</a>
            <a href="#como" className="ah-btn-ghost">Como funciona</a>
          </div>
        </div>
        <MockupAthenia />
      </div>
    </section>
  );
}

function SobreSection() {
  return (
    <section className="ah-section ah-sobre">
      <div className="ah-wrap">
        <div className="ah-sobre-grid ah-reveal">
          <div>
            <span className="ah-tag">O Produto</span>
            <h2 className="ah-title">O que é AthenIA</h2>
            <p className="ah-text">
              AthenIA é o cérebro analítico do ecossistema SummerTech. Ela coleta, organiza
              e interpreta dados de todos os seus produtos — e de fontes externas como CRMs —
              exibindo tudo em um dashboard intuitivo com alertas inteligentes e relatórios
              automáticos. Chega de planilhas manuais e dados espalhados.
            </p>
          </div>
          <div className="ah-sobre-cards">
            {[
              { icon: "🔗", title: "Integração completa", text: "Conecta LeadIA, NutrIA, JulIA e CRMs externos em uma única fonte de verdade." },
              { icon: "⚡", title: "Insights em tempo real", text: "Dashboard ao vivo com alertas automáticos quando algo muda no seu desempenho." },
              { icon: "📄", title: "Relatórios automáticos", text: "Relatórios semanais e mensais em PDF gerados e enviados sem intervenção manual." },
            ].map((c) => (
              <div className="ah-info-card" key={c.title}>
                <div className="ah-info-icon">{c.icon}</div>
                <div>
                  <div className="ah-info-title">{c.title}</div>
                  <div className="ah-info-text">{c.text}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function ComoSection() {
  const steps = [
    { icon: <FaBolt />, num: "01", title: "Conecta as fontes", desc: "AthenIA integra automaticamente com LeadIA, NutrIA, JulIA e seu CRM — centralizando todos os dados em um lugar." },
    { icon: <FaChartBar />, num: "02", title: "Analisa e exibe", desc: "Dashboard em tempo real com métricas de cada etapa do funil — de leads captados a conversões finalizadas." },
    { icon: <FaBrain />, num: "03", title: "Gera insights e alertas", desc: "A IA identifica padrões, oportunidades e quedas de desempenho — e notifica você antes que virem problemas." },
  ];
  return (
    <section className="ah-section ah-como" id="como">
      <div className="ah-wrap">
        <span className="ah-tag">Como Funciona</span>
        <h2 className="ah-title">Três etapas.<br />Visão total do negócio.</h2>
        <div className="ah-steps">
          {steps.map((s) => (
            <div className="ah-step ah-reveal" key={s.num}>
              <div className="ah-step-num">{s.num}</div>
              <span className="ah-step-icon">{s.icon}</span>
              <div className="ah-step-title">{s.title}</div>
              <div className="ah-step-desc">{s.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function BeneficiosSection() {
  const bens = [
    { num: "360°", title: "Visão completa do funil", text: "Do primeiro lead ao fechamento — tudo em um único painel, sem precisar abrir várias ferramentas." },
    { num: "ROI", title: "Comprove o resultado", text: "Relatórios claros que mostram exatamente o retorno gerado por cada produto contratado." },
    { num: "IA", title: "Alertas antes dos problemas", text: "A inteligência artificial detecta quedas e oportunidades antes que você precise procurar." },
    { num: "Auto", title: "Relatórios sem esforço", text: "PDFs profissionais gerados e enviados automaticamente — prontos para apresentar ao time ou diretores." },
  ];
  return (
    <section className="ah-section ah-beneficios">
      <div className="ah-wrap">
        <span className="ah-tag">Benefícios</span>
        <h2 className="ah-title">Por que AthenIA<br />transforma sua gestão</h2>
        <div className="ah-ben-grid">
          {bens.map((b) => (
            <div className="ah-ben-card ah-reveal" key={b.title}>
              <span className="ah-ben-num">{b.num}</span>
              <div className="ah-ben-title">{b.title}</div>
              <div className="ah-ben-text">{b.text}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function SegurancaSection() {
  const items = [
    "Dados criptografados em trânsito e em repouso.",
    "Acesso por usuário com permissões configuráveis.",
    "Logs de auditoria de todos os acessos ao painel.",
    "Exportação de dados a qualquer momento.",
    "Tratamento de dados em conformidade com a LGPD.",
  ];
  return (
    <section className="ah-section ah-seg">
      <div className="ah-wrap">
        <div className="ah-seg-inner ah-reveal">
          <div className="ah-seg-quote">
            "Seus dados centralizados, seguros e sempre disponíveis — com inteligência que trabalha por você."
          </div>
          <div>
            <span className="ah-tag">Segurança e Compliance</span>
            <div className="ah-seg-items">
              {items.map((item) => (
                <div className="ah-seg-item" key={item}>
                  <FaShieldAlt className="ah-seg-item-icon" />
                  {item}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function PricingSection() {
  const waBase = "https://wa.me/5500000000000?text=";
  return (
    <section className="ah-section ah-pricing">
      <div className="ah-wrap">
        <span className="ah-tag">Planos</span>
        <h2 className="ah-title">Piloto rápido.<br />Escale depois.</h2>
        <p className="ah-pricing-subtitle">
          Comece com nosso piloto gratuito e veja a inteligência do seu negócio em ação — antes de qualquer compromisso.
        </p>
        <div className="ah-plans">
          {plans.map((p) => (
            <article
              className={`ah-plan ah-reveal ${p.popular ? "popular" : ""} ${p.included ? "included" : ""}`}
              key={p.name}
            >
              {p.badge && (
                <div className={`ah-plan-badge ${p.badge.style}`}>{p.badge.text}</div>
              )}
              <div className="ah-plan-name">{p.name}</div>
              <div className="ah-plan-price">{p.price}</div>
              <div className="ah-plan-note">{p.note}</div>
              <ul className="ah-plan-features">
                {p.features.map((f) => (
                  <li key={f}>
                    <span className="ah-plan-check">✔</span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <a
                href={`${waBase}${encodeURIComponent(p.waMessage)}`}
                target="_blank"
                rel="noreferrer"
                className="ah-plan-btn"
                style={{ backgroundColor: p.color, color: p.textColor }}
              >
                {p.cta}
              </a>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function FaqSection() {
  return (
    <section className="ah-section ah-faq">
      <div className="ah-wrap">
        <span className="ah-tag">Dúvidas</span>
        <h2 className="ah-title">Perguntas &<br />Respostas</h2>
        <div className="ah-faq-grid">
          {faqs.map((f) => (
            <div className="ah-faq-card ah-reveal" key={f.q}>
              <div className="ah-faq-q">{f.q}</div>
              <div className="ah-faq-a">{f.a}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CtaSection() {
  const waMessage = "Gostaria de ativar o produto AthenIA no meu negócio";
  return (
    <section className="ah-section ah-cta" id="cta">
      <div className="ah-wrap">
        <div className="ah-cta-inner">
          <h2>Ative AthenIA<br />e enxergue seu negócio.</h2>
          <p>
            Centralize seus dados, receba insights automáticos e tome decisões
            com inteligência — tudo em um único painel.
          </p>
          <BotaoContato className="ah-btn-dark" text="Ativar AthenIA agora →" mensagem={waMessage} />
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────
   PAGE
───────────────────────────────────────── */
export default function AthenIAPage() {
  useReveal();

  return (
    <>
      <Navbar />
      <HeroSection />
      <SobreSection />
      <ComoSection />
      <BeneficiosSection />
      <SegurancaSection />
      <PricingSection />
      <FaqSection />
      <CtaSection />
      <Footer20 className="ah-footer" voltarInicio="true" />
    </>
  );
}